import type { TranslateParams } from './appUiMessages'

/** Maps legacy English breadcrumb labels to dashboard.json keys (params via pattern). */
const STATIC_LABEL_KEYS: Record<string, string> = {
  Dashboard: 'breadcrumbs.dashboard',
  Home: 'breadcrumbs.customerHome',
  Catalog: 'breadcrumbs.customerCatalog',
  Cart: 'breadcrumbs.customerCart',
  Orders: 'breadcrumbs.customerOrders',
  Subscriptions: 'breadcrumbs.customerSubscriptions',
  Schedule: 'breadcrumbs.schedule',
  Profile: 'breadcrumbs.customerProfile',
  'Register payment': 'breadcrumbs.registerPayment',
  Details: 'breadcrumbs.details',
  Customers: 'breadcrumbs.customers',
  Products: 'breadcrumbs.products',
  'Sales Orders': 'breadcrumbs.orders',
  Quotations: 'breadcrumbs.quotations',
  Payments: 'breadcrumbs.payments',
  Staff: 'breadcrumbs.staff',
  Brands: 'breadcrumbs.brands',
  Suppliers: 'breadcrumbs.suppliers',
  'Purchase Orders': 'breadcrumbs.purchaseOrders',
  Subscription: 'breadcrumbs.subscription',
  Tags: 'breadcrumbs.tags',
  Website: 'breadcrumbs.website',
  'Events & Tickets': 'breadcrumbs.events',
  Forms: 'breadcrumbs.forms',
  Messaging: 'breadcrumbs.messaging',
  Templates: 'breadcrumbs.templates',
  'Send message': 'breadcrumbs.sendMessage',
  Shops: 'breadcrumbs.shops',
  Branches: 'breadcrumbs.branches',
  'Team Files': 'breadcrumbs.teamFiles',
  'Collaboration Board': 'breadcrumbs.collaboration',
  Payroll: 'breadcrumbs.payroll',
  'Group Packages': 'breadcrumbs.classPackages',
  'Class Packages': 'breadcrumbs.classPackages',
  'Automation Tasks': 'breadcrumbs.automationTasks',
  Journal: 'breadcrumbs.journal',
  Bookings: 'breadcrumbs.bookings',
  'Data Management': 'breadcrumbs.dataManagement',
  Reports: 'breadcrumbs.reports',
  'Your profile': 'breadcrumbs.profile',
  Settings: 'breadcrumbs.settings',
  'Shop quota (admin)': 'breadcrumbs.shopQuotaAdmin',
  'Host backups': 'breadcrumbs.hostBackups',
  Teams: 'breadcrumbs.teams',
  Coupons: 'breadcrumbs.coupons',
  Membership: 'breadcrumbs.membership',
  Referrers: 'breadcrumbs.referrers',
  Statistics: 'breadcrumbs.statistics',
  'New customer': 'breadcrumbs.newCustomer',
  'New product': 'breadcrumbs.newProduct',
  'New order': 'breadcrumbs.newOrder',
  'New payment': 'breadcrumbs.newPayment',
  'New staff': 'breadcrumbs.newStaff',
  'New shop': 'breadcrumbs.newShop',
  'New supplier': 'breadcrumbs.newSupplier',
  'New purchase order': 'breadcrumbs.newPurchaseOrder',
  'New tag': 'breadcrumbs.newTag',
  'New form': 'breadcrumbs.newForm',
  'New template': 'breadcrumbs.newTemplate',
  'New grant rule': 'breadcrumbs.newGrantRule',
  'New team': 'breadcrumbs.newTeam',
  'New entry': 'breadcrumbs.newEntry',
  'New session': 'breadcrumbs.newSession',
  'New event': 'breadcrumbs.newEvent',
  'New address': 'breadcrumbs.newAddress',
  'Trial balance': 'breadcrumbs.trialBalance',
  'Journal register': 'breadcrumbs.journalRegister',
  'New account': 'breadcrumbs.newAccount',
  'Customer Product Reconcile Report': 'breadcrumbs.customerProductReconcile',
  'Subscription Report': 'breadcrumbs.subscriptionReport',
  'Platform admin': 'breadcrumbs.platformAdmin',
  'Support Chat': 'breadcrumbs.supportChat',
  'ShopQuay Support': 'breadcrumbs.shopQuaySupport',
  'Platform support tickets': 'breadcrumbs.platformTicketQueue',
}

type PatternRule = {
  re: RegExp
  key: string
  params?: (m: RegExpMatchArray) => TranslateParams
}

const PATTERN_RULES: PatternRule[] = [
  { re: /^Edit customer #(\d+)$/, key: 'breadcrumbs.editCustomer', params: (m) => ({ id: m[1] }) },
  { re: /^Customer #(\d+)$/, key: 'breadcrumbs.customerId', params: (m) => ({ id: m[1] }) },
  { re: /^Edit product #(\d+)$/, key: 'breadcrumbs.editProduct', params: (m) => ({ id: m[1] }) },
  { re: /^Product #(\d+)$/, key: 'breadcrumbs.productId', params: (m) => ({ id: m[1] }) },
  { re: /^Edit order #(\d+)$/, key: 'breadcrumbs.editOrder', params: (m) => ({ id: m[1] }) },
  { re: /^Order #(\d+)$/, key: 'breadcrumbs.orderId', params: (m) => ({ id: m[1] }) },
  { re: /^Edit payment #(\d+)$/, key: 'breadcrumbs.editPayment', params: (m) => ({ id: m[1] }) },
  { re: /^Payment #(\d+)$/, key: 'breadcrumbs.paymentId', params: (m) => ({ id: m[1] }) },
  { re: /^Edit shop #(\d+)$/, key: 'breadcrumbs.editShop', params: (m) => ({ id: m[1] }) },
  { re: /^Shop #(\d+)$/, key: 'breadcrumbs.shopId', params: (m) => ({ id: m[1] }) },
  { re: /^Edit #(\d+)$/, key: 'breadcrumbs.editId', params: (m) => ({ id: m[1] }) },
  { re: /^Staff #(\d+)$/, key: 'breadcrumbs.staffId', params: (m) => ({ id: m[1] }) },
  { re: /^Supplier #(\d+)$/, key: 'breadcrumbs.supplierId', params: (m) => ({ id: m[1] }) },
  { re: /^Edit PO #(\d+)$/, key: 'breadcrumbs.editPo', params: (m) => ({ id: m[1] }) },
  { re: /^PO #(\d+)$/, key: 'breadcrumbs.poId', params: (m) => ({ id: m[1] }) },
  { re: /^Edit tag #(\d+)$/, key: 'breadcrumbs.editTag', params: (m) => ({ id: m[1] }) },
  { re: /^Tag #(\d+)$/, key: 'breadcrumbs.tagId', params: (m) => ({ id: m[1] }) },
  { re: /^Page #(\d+)$/, key: 'breadcrumbs.pageId', params: (m) => ({ id: m[1] }) },
  { re: /^Edit event #(\d+)$/, key: 'breadcrumbs.editEvent', params: (m) => ({ id: m[1] }) },
  { re: /^Event #(\d+) · tickets$/, key: 'breadcrumbs.eventTickets', params: (m) => ({ id: m[1] }) },
  { re: /^Edit form #(\d+)$/, key: 'breadcrumbs.editForm', params: (m) => ({ id: m[1] }) },
  { re: /^Form #(\d+) · submissions$/, key: 'breadcrumbs.formSubmissions', params: (m) => ({ id: m[1] }) },
  { re: /^Edit template #(\d+)$/, key: 'breadcrumbs.editTemplate', params: (m) => ({ id: m[1] }) },
  { re: /^Template #(\d+)$/, key: 'breadcrumbs.templateId', params: (m) => ({ id: m[1] }) },
  { re: /^Edit rule #(\d+)$/, key: 'breadcrumbs.editRule', params: (m) => ({ id: m[1] }) },
  { re: /^Team #(\d+) members$/, key: 'breadcrumbs.teamMembers', params: (m) => ({ id: m[1] }) },
  { re: /^Entry #(\d+)$/, key: 'breadcrumbs.entryId', params: (m) => ({ id: m[1] }) },
  { re: /^Edit session #(\d+)$/, key: 'breadcrumbs.editSession', params: (m) => ({ id: m[1] }) },
  { re: /^Session #(\d+)$/, key: 'breadcrumbs.sessionId', params: (m) => ({ id: m[1] }) },
  { re: /^Booking #(\d+)$/, key: 'breadcrumbs.bookingId', params: (m) => ({ id: m[1] }) },
  { re: /^Edit address #(\d+)$/, key: 'breadcrumbs.editAddress', params: (m) => ({ id: m[1] }) },
  { re: /^#(\d+)$/, key: 'breadcrumbs.idOnly', params: (m) => ({ id: m[1] }) },
]

export function translateBreadcrumbLabel(
  label: string,
  t: (key: string, params?: TranslateParams) => string,
): string {
  const staticKey = STATIC_LABEL_KEYS[label]
  if (staticKey) {
    const translated = t(staticKey)
    if (translated !== staticKey) return translated
  }
  for (const rule of PATTERN_RULES) {
    const m = label.match(rule.re)
    if (m) {
      const translated = t(rule.key, rule.params?.(m))
      if (translated !== rule.key) return translated
    }
  }
  return label
}
